<script setup>
/* global defineProps */
import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import Tag from 'primevue/tag';
import SnatTicketButton from '@/components/SnatTicketButton.vue';
import {
  reqNotebookItems,
  reqNotebookPickActive,
  reqNotebookScreenNote,
  clearRequirementsNotebook,
  cancelRequirementPickMode,
  startRequirementPickMode,
  formatRequirementsNotebookMarkdown,
  removeRequirementItem,
} from '@/utils/requirementsNotebook.js';

const props = defineProps({
  /** Cuando es true, solo el contenido (sin pestaña flotante); pensado para el panel SNAT unificado. */
  embedded: { type: Boolean, default: false },
});

const toast = useToast();
const route = useRoute();
const expanded = ref(false);

watch(
  () => props.embedded,
  (v) => {
    if (v) expanded.value = true;
  },
  { immediate: true }
);

const count = computed(() => reqNotebookItems.value.length);
const previewText = computed(() => formatRequirementsNotebookMarkdown(route));
const snatPrefillSubject = computed(() => {
  const routeName = route?.name != null ? String(route.name) : 'sin-ruta';
  return `KOSEXA Tienda UI (${routeName}): nuevo requerimiento`;
});

function updateItemNote(id, text) {
  const i = reqNotebookItems.value.findIndex((x) => x.id === id);
  if (i === -1) return;
  const next = [...reqNotebookItems.value];
  next[i] = { ...next[i], note: text };
  reqNotebookItems.value = next;
}

async function copyMarkdown() {
  const text = formatRequirementsNotebookMarkdown(route);
  try {
    await navigator.clipboard.writeText(text);
    toast.add({
      severity: 'success',
      summary: 'Markdown copiado',
      detail: 'Péguelo en el chat con el agente programador o en un ticket SNAT.',
      life: 4000,
    });
  } catch {
    toast.add({
      severity: 'error',
      summary: 'No se pudo copiar',
      detail: 'Seleccione el texto de la vista previa.',
      life: 5000,
    });
  }
}

function onStartPick() {
  if (reqNotebookPickActive.value) {
    cancelRequirementPickMode();
    toast.add({ severity: 'info', summary: 'Selección cancelada', life: 2000 });
    return;
  }
  expanded.value = true;
  startRequirementPickMode();
  toast.add({
    severity: 'info',
    summary: 'Modo selección',
    detail: 'Un clic en la pantalla captura el elemento. Escape cancela.',
    life: 5000,
  });
}

function onClear() {
  clearRequirementsNotebook();
  toast.add({ severity: 'success', summary: 'Cuaderno de requerimientos vaciado', life: 2000 });
}

onUnmounted(() => {
  cancelRequirementPickMode();
});
</script>

<template>
  <div
    class="req-notebook-panel rn-panel"
    :class="{ 'rn-panel--embedded': embedded }"
    aria-label="Cuaderno de requerimientos UI"
  >
    <button v-if="!embedded" type="button" class="rn-tab" @click="expanded = !expanded">
      <span class="rn-tab-label">Req. UI → SNAT</span>
      <Tag v-if="count" :value="String(count)" severity="warning" class="rn-count" />
      <Tag v-else value="0" severity="secondary" class="rn-count" />
      <i :class="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-up'" />
    </button>

    <Transition :name="embedded ? 'rn-embed' : 'rn-slide'">
      <div v-show="embedded || expanded" class="rn-sheet" :class="{ 'rn-sheet--embedded': embedded }">
        <p class="rn-intro">
          Recorra las pantallas y anote cambios: <strong>nota general</strong> de la vista y, con
          <strong>Seleccionar elemento</strong>, un clic sobre un control para vincular texto al DOM. Use
          <strong>Copiar Markdown</strong> o <strong>Agregar ticket SNAT</strong> para enviarlo al mismo backend que el
          resto de clientes SNAT.
        </p>
        <div v-if="reqNotebookPickActive" class="rn-pick-banner">
          <Tag value="Clic en la UI para capturar · Esc cancela" severity="warning" />
        </div>
        <div class="rn-actions">
          <Button
            :label="reqNotebookPickActive ? 'Cancelar selección' : 'Seleccionar elemento'"
            :icon="reqNotebookPickActive ? 'pi pi-times' : 'pi pi-crosshairs'"
            class="p-button-sm"
            :severity="reqNotebookPickActive ? 'secondary' : 'help'"
            @click="onStartPick"
          />
          <SnatTicketButton
            source-module="kosexa_front_requirements_notebook"
            :prefill-subject="snatPrefillSubject"
            :prefill-description="previewText"
            kind-default="feature"
            label="Agregar ticket SNAT"
            button-class="p-button-sm p-button-help"
            offer-clear-requirements-after-success
          />
          <Button label="Vaciar" icon="pi pi-trash" class="p-button-sm p-button-text" @click="onClear" />
          <Button label="Copiar Markdown" icon="pi pi-copy" class="p-button-sm p-button-secondary" @click="copyMarkdown" />
        </div>

        <label class="rn-label">Nota general (pantalla actual; se puede editar al cambiar de ruta)</label>
        <Textarea
          v-model="reqNotebookScreenNote"
          class="rn-text rn-screen"
          rows="3"
          auto-resize
          placeholder="Ej.: mover bloque de filtros, textos de ayuda, validaciones nuevas…"
        />

        <div v-if="reqNotebookItems.length" class="rn-items">
          <p class="rn-items-title">Elementos ({{ reqNotebookItems.length }})</p>
          <div v-for="it in reqNotebookItems" :key="it.id" class="rn-item">
            <div class="rn-item-head">
              <span class="rn-item-meta" :title="it.selector">{{ it.elementLabel || it.selector }}</span>
              <Button
                icon="pi pi-times"
                class="p-button-rounded p-button-text p-button-sm"
                aria-label="Quitar"
                @click="removeRequirementItem(it.id)"
              />
            </div>
            <code class="rn-item-sel">{{ it.selector }}</code>
            <p class="rn-item-route"><small>Capturado en: {{ it.routePath }}</small></p>
            <Textarea
              :model-value="it.note"
              class="rn-text"
              rows="2"
              auto-resize
              placeholder="Qué debe hacer el programador con este elemento…"
              @update:model-value="(v) => updateItemNote(it.id, v)"
            />
          </div>
        </div>
        <p v-else class="rn-empty">Sin elementos. Active «Seleccionar elemento» y haga clic en la UI.</p>

        <label class="rn-label rn-label--mono">Vista previa (Markdown)</label>
        <Textarea :model-value="previewText" class="rn-text rn-preview" readonly rows="10" />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* Paleta propia (azul “herramienta SNAT”), distinta del tema tienda para que se entienda que es un panel aparte */
.rn-panel--embedded {
  position: static;
  z-index: auto;
  left: auto;
  bottom: auto;
  max-width: none;
  pointer-events: auto;
}

.rn-panel--embedded > .rn-sheet--embedded {
  margin: 0;
  max-height: none;
  box-shadow: none;
  border-radius: 8px;
  border: 1px solid rgba(3, 105, 161, 0.25);
  background: rgba(255, 255, 255, 0.5);
}

.rn-panel {
  --rn-blue-ink: #0c2d48;
  --rn-blue-deep: #0a4d7a;
  --rn-blue-mid: #0369a1;
  --rn-blue-soft: #bae6fd;
  --rn-blue-mist: #e0f2fe;
  --rn-blue-glow: #7dd3fc;

  position: fixed;
  z-index: 10040;
  left: 0;
  bottom: 0;
  max-width: min(520px, 100vw);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  pointer-events: none;
}

.rn-panel > * {
  pointer-events: auto;
}

.rn-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem;
  margin: 0 0 0.75rem 0.75rem;
  border: 1px solid var(--rn-blue-deep);
  border-radius: 8px 8px 0 0;
  border-bottom: none;
  background: linear-gradient(165deg, var(--rn-blue-mid) 0%, var(--rn-blue-deep) 55%, #082f49 100%);
  box-shadow: 0 -6px 28px rgba(2, 48, 71, 0.35);
  cursor: pointer;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  color: #f0f9ff;
}

.rn-tab:hover {
  background: linear-gradient(165deg, #0284c7 0%, var(--rn-blue-mid) 50%, var(--rn-blue-deep) 100%);
  color: #fff;
}

.rn-tab-label {
  white-space: nowrap;
}

.rn-tab :deep(.p-tag) {
  background: rgba(240, 249, 255, 0.2) !important;
  color: #f0f9ff !important;
  border: 1px solid rgba(186, 230, 253, 0.45);
}

.rn-count {
  font-size: 0.7rem !important;
  padding: 0.15rem 0.45rem !important;
}

.rn-sheet {
  width: 100%;
  max-height: min(72vh, 620px);
  display: flex;
  flex-direction: column;
  padding: 0.75rem 0.85rem 1rem;
  margin: 0 0 0 0.75rem;
  background: linear-gradient(180deg, var(--rn-blue-mist) 0%, var(--rn-blue-soft) 120%);
  border: 1px solid var(--rn-blue-mid);
  border-radius: 0 10px 0 0;
  box-shadow: 0 -10px 36px rgba(3, 105, 161, 0.22);
  overflow: auto;
  color: var(--rn-blue-ink);
}

.rn-intro {
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  color: #164e63;
  line-height: 1.45;
}

.rn-intro strong {
  color: var(--rn-blue-deep);
}

.rn-pick-banner {
  margin-bottom: 0.5rem;
}

.rn-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.65rem;
}

.rn-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--rn-blue-deep);
  margin-bottom: 0.25rem;
}

.rn-label--mono {
  margin-top: 0.75rem;
}

.rn-text {
  width: 100%;
  font-size: 0.8rem;
  line-height: 1.4;
  resize: vertical;
}

.rn-sheet :deep(.rn-text.p-inputtextarea),
.rn-sheet :deep(.rn-text textarea) {
  background: rgba(255, 255, 255, 0.82) !important;
  border: 1px solid var(--rn-blue-glow) !important;
  color: var(--rn-blue-ink) !important;
  border-radius: 8px !important;
}

.rn-sheet :deep(.rn-text.p-inputtextarea:enabled:hover),
.rn-sheet :deep(.rn-text textarea:hover) {
  border-color: var(--rn-blue-mid) !important;
}

.rn-screen {
  margin-bottom: 0.75rem;
}

.rn-preview {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.72rem;
  min-height: 140px;
}

.rn-sheet :deep(.rn-preview.p-inputtextarea),
.rn-sheet :deep(.rn-preview textarea) {
  background: rgba(255, 255, 255, 0.55) !important;
  border: 1px dashed var(--rn-blue-mid) !important;
  color: #0f172a !important;
}

.rn-items-title {
  margin: 0 0 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--rn-blue-deep);
}

.rn-item {
  border: 1px solid rgba(3, 105, 161, 0.35);
  border-radius: 8px;
  padding: 0.5rem 0.5rem 0.65rem;
  margin-bottom: 0.5rem;
  background: rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(4px);
}

.rn-item-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.35rem;
}

.rn-item-meta {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--rn-blue-ink);
  line-height: 1.3;
  word-break: break-word;
}

.rn-item-sel {
  display: block;
  font-size: 0.65rem;
  margin: 0.25rem 0 0;
  color: #075985;
  word-break: break-all;
  white-space: pre-wrap;
}

.rn-item-route {
  margin: 0.15rem 0 0.35rem;
  color: #0e7490;
}

.rn-empty {
  margin: 0.35rem 0 0.75rem;
  font-size: 0.78rem;
  color: #155e75;
}

.rn-slide-enter-active,
.rn-slide-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.rn-slide-enter-from,
.rn-slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.rn-embed-enter-active,
.rn-embed-leave-active {
  transition: none;
}
</style>

<style>
body.req-pick-active {
  cursor: crosshair !important;
}
</style>
